import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Zap,
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  Plus,
  Minus,
  ArrowRight,
  Search,
  ShieldPlus,
  AlertCircle,
  UserCheck,
} from "lucide-react";
import Footer from "../../components/Footer";
const emailSecurityFailureCards = [
  {
    title: "MODERN EMAIL ATTACKS LOOK LEGITIMATE",
    points: [
      "Phishing uses real brands and valid domains",
      "AI-written messages evade signature detection",
      "Business Email Compromise abuses trust instead of malware",
    ],
    icon: (
      <AlertCircle className="text-[#34d399]" size={32} strokeWidth={1.5} />
    ),
  },
  {
    title: "EMAIL SECURITY STOPS AT DELIVERY",
    points: [
      "Links are weaponized after delivery",
      "Conversations get hijacked mid-thread",
      "Traditional gateways miss post-delivery threats",
    ],
    icon: <Search className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
  {
    title: "OUTBOUND EMAIL IS A BLIND SPOT",
    points: [
      "Data leakage through mis-sent emails",
      "Compromised accounts sending malicious messages",
      "Lack of monitoring for internal email activity",
    ],
    icon: <ShieldPlus className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
  {
    title: "INCIDENT RESPONSE STARTS TOO LATE",
    points: [
      "Stolen credentials enable account takeover",
      "Fraudulent payments may already be initiated",
      "Security teams lack visibility for rapid investigation",
    ],
    icon: <UserCheck className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
];
// --- 2. BEAUTIFIED PROCESS DATA (Restored Full Text) ---
const steps = [
  {
    title: "Continuous Email Threat Visibility",
    desc: "We continuously monitor inbound, outbound, and internal flows, reviewing threats that bypass automated controls.",
    owns: [
      "Continuous review of high-risk emails",
      "Visibility into social engineering attempts",
      "Monitoring of internal and lateral threats",
    ],
  },
  {
    title: "Advanced Phishing & Impersonation Detection",
    desc: "We identify attacks mimicking executives, vendors, and partners using lookalike domains and conversation hijacking analysis.",
    owns: [
      "Detection of display name and domain spoofing",
      "Analysis of business context and intent",
      "Validation of suspicious sender behavior",
    ],
  },
  {
    title: "Post-Delivery Monitoring & Response",
    desc: "We monitor emails after delivery for weaponized links or changes in file reputation, remediating risk quickly.",
    owns: [
      "Monitoring links and attachments post-delivery",
      "Identification of delayed-activation threats",
      "Guided quarantine and removal actions",
    ],
  },
  {
    title: "Outbound Email & Account Oversight",
    desc: "We monitor outbound activity to detect compromised accounts and data leakage before wider damage occurs.",
    owns: [
      "Monitoring abnormal outbound behavior",
      "Detection of suspicious sending patterns",
      "Identification of sensitive data exposure",
    ],
  },
  {
    title: "Human-Centric Risk Analysis",
    desc: "We analyze user interactions like repeated clicks or credential submissions to identify targeted attacks.",
    owns: [
      "Review of risky user email interactions",
      "Correlation of email risk with access behavior",
      "Escalation of systemic human risk patterns",
    ],
  },
  {
    title: "Email-Centric Incident Response",
    desc: "When incidents occur, we contain impact and provide forensic timeline reconstruction for security leadership.",
    owns: [
      "Guided response to phishing and BEC",
      "Email attack timeline reconstruction",
      "Clear reporting for audit and executives",
    ],
  },
];

// --- 3. KEY BENEFITS ---
const benefits = [
  {
    title: "Real-Time Threat Visibility",
    desc: "Understand what threats are targeting your organization and how attacks evolve.",
  },
  {
    title: "Reduced BEC Risk",
    desc: "Stop credential theft and invoice fraud before money or data is lost.",
  },
  {
    title: "Protection Beyond Gateway",
    desc: "Address post-delivery threats that traditional security misses.",
  },
  {
    title: "Contained Compromise",
    desc: "Stop compromised mailboxes from spreading attacks or leaking data.",
  },
  {
    title: "Leadership-Ready Clarity",
    desc: "Move from guesswork to clear forensic answers on impact and response.",
  },
];

// --- 4. FAQ DATA ---
const faqItems = [
  {
    q: "Why is Managed Email Security different from traditional SEG?",
    a: "Standard Secure Email Gateways (SEGs) are 'set-and-forget.' Managed Email Security provides 24/7 human-led investigation and post-delivery response.",
  },
  {
    q: "Can you stop Business Email Compromise (BEC)?",
    a: "Yes. By analyzing the context of conversations and detecting lookalike domains, we identify the impersonation attempts that define BEC.",
  },
  {
    q: "Does this integrate with Microsoft 365 or Google Workspace?",
    a: "Absolutely. We work directly with your cloud email provider to monitor traffic without requiring complex architectural changes.",
  },
];

export default function ManagedEmailSecurity() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Managed Email Security Services | Synthera Security</title>

        <meta
          name="description"
          content="Managed Email Security services providing phishing protection, business email compromise prevention, post-delivery monitoring, account takeover detection, and email threat response."
        />

        <meta
          name="keywords"
          content="Managed Email Security, Email Security, Phishing Protection, Business Email Compromise, BEC Prevention, Email Threat Detection, Microsoft 365 Security, Google Workspace Security, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Managed Email Security Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Protect your organization from phishing, BEC attacks, account compromise, and advanced email threats with managed email security services."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/managed-email-security"
        />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/managed-email-security"
        />

        <meta
          property="og:image"
          content="https://images.unsplash.com/photo-1557200134-90327ee9fafa?q=80&w=2070&auto=format&fit=crop"
        />
      </Helmet>

      <div className="bg-white min-h-screen font-sans text-slate-900">
        {/* --- HERO SECTION --- */}
        <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6">
          <Link
            to="/services/managed-security-services"
            className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
          >
            <ArrowLeft size={24} />
          </Link>

          <div className="text-center max-w-5xl mb-16">
            <span className="text-white/40 text-xs tracking-[0.2em] uppercase font-bold mb-4 block">
              Email is the #1 threat vector
            </span>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight tracking-tight">
              Traditional email security fails -{" "}
              <span className="text-[#24ff81]">risks stay unmanaged</span>
            </h1>
          </div>

          <div className="container mx-auto max-w-7xl relative z-10 mb-32">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {emailSecurityFailureCards.map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="group bg-white p-6 rounded-3xl shadow-lg flex flex-col min-h-[260px] cursor-pointer transition-all hover:shadow-2xl"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-[#34d399] text-base font-bold uppercase tracking-wide mb-4 group-hover:text-[#0c3f38] transition">
                    {item.title}
                  </h3>
                  <div className="flex flex-col w-full">
                    {item.points.map((line, i) => (
                      <div key={i}>
                        <p className="text-[#0c3f38]/70 text-sm font-medium py-1 group-hover:text-[#0c3f38] transition">
                          {line}
                        </p>
                        {i !== item.points.length - 1 && (
                          <div className="h-px w-full bg-[#24ff81]/40 my-[2px]" />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* --- SERVICE OVERVIEW SECTION --- */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-[#032d28] text-4xl md:text-5xl font-black uppercase mb-8 leading-tight tracking-tight">
                  Managed Email Security
                </h2>
                <div className="space-y-6 text-slate-600 text-lg leading-relaxed font-medium">
                  <p>
                    Email security fails not because tools are missing, but
                    because no one continuously operates, investigates, and
                    responds.
                  </p>
                  <p className="bg-slate-50 p-6 rounded-2xl border-l-4 border-[#24ff81]">
                    Our service focuses on ongoing detection, validation, and
                    response moving beyond set-and-forget filtering to active
                    inbox defense.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    {[
                      "Phishing Defense",
                      "BEC Prevention",
                      "Post-Delivery Response",
                      "Account Takeover Protection",
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 text-[#032d28] font-bold text-base uppercase tracking-tight"
                      >
                        <CheckCircle2 size={20} className="text-[#24ff81]" />{" "}
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
              <div className="relative group">
                <div className="absolute inset-0 bg-[#24ff81]/10 rounded-[48px] rotate-3 group-hover:rotate-0 transition-transform duration-500" />
                <img
                  src="https://images.unsplash.com/photo-1557200134-90327ee9fafa?q=80&w=2070&auto=format&fit=crop"
                  className="relative rounded-[48px] shadow-2xl w-full h-[500px] object-cover"
                  alt="Email Security Operations"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== PREMIUM OPERATIONAL CARDS (Exact Forest Style Match) ===== */}
        <section className="relative py-28 bg-[#071c18] overflow-hidden">
          {/* Signature radial glow overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(54,168,138,.15),transparent)]" />

          <div className="container mx-auto px-6 max-w-7xl relative z-10">
            <div className="text-center mb-20">
              <p className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-bold mb-6">
                Managed Email Operations
              </p>
              <h2 className="text-white text-4xl md:text-6xl font-black leading-tight">
                Operational{" "}
                <span className="text-emerald-400">Execution Model</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="forest-card rounded-3xl p-8 group cursor-pointer"
                >
                  {/* Icon Container - Email/Communication Focused */}
                  <div className="icon-container w-14 h-14 rounded-2xl flex items-center justify-center mb-8">
                    <CheckCircle2 className="text-emerald-400" size={26} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-emerald-300 transition">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-emerald-100/60 text-sm leading-relaxed mb-8">
                    {step.desc}
                  </p>

                  {/* Divider & Service Ownership */}
                  <div className="border-t border-emerald-800/30 pt-6">
                    <p className="text-xs font-semibold text-emerald-500/70 uppercase tracking-widest mb-5">
                      Service Ownership
                    </p>

                    <ul className="space-y-3">
                      {step.owns.map((item, i) => (
                        <li
                          key={i}
                          className="service-item flex items-center text-sm text-emerald-100/80 group-hover:text-white transition-colors"
                        >
                          <ChevronRight
                            className="mr-3 text-emerald-400"
                            size={16}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Hover Arrow Indicator */}
                  <div className="mt-8 flex justify-end">
                    <ArrowRight
                      className="text-emerald-400 opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all duration-300"
                      size={20}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* ================= KEY BENEFITS PREMIUM BALANCED ================= */}
        <section className="py-28 bg-[#071c18] relative overflow-hidden">
          {/* subtle grid */}
          <div
            className="absolute inset-0 opacity-10 
    bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
    linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
    bg-[size:40px_40px]"
          />

          {/* heading */}
          <div className="max-w-7xl mx-auto text-center mb-20 px-6 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight">
              Key Benefits
            </h2>

            <p className="text-emerald-100/60 text-lg max-w-2xl mx-auto">
              Stop advanced email threats, reduce compromise risk, and gain
              clear visibility into every incident.
            </p>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* ROW 1 — first 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {benefits.slice(0, 3).map((b, i) => (
                <div
                  key={i}
                  className="group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />

                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* ROW 2 — center 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 mt-0 md:max-w-3xl mx-auto">
              {benefits.slice(3, 5).map((b, i) => (
                <div
                  key={i}
                  className="group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />

                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- FAQ SECTION --- */}
        <section className="bg-[#071c18] py-28 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[#065c41] text-4xl md:text-6xl font-semibold text-center mb-16 uppercase tracking-tighter">
              FAQ
            </h2>
            <div className="space-y-0 border-t border-white/10">
              {faqItems.map((item, index) => (
                <div key={index} className="border-b border-white/10">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full py-8 flex items-center justify-between text-left group"
                  >
                    <span className="text-white text-lg md:text-xl font-medium leading-snug group-hover:text-[#24ff81] transition-colors">
                      {item.q}
                    </span>
                    <div className="ml-4 flex-shrink-0">
                      {openFaq === index ? (
                        <Minus size={28} className="text-[#24ff81]" />
                      ) : (
                        <Plus size={28} className="text-[#24ff81]" />
                      )}
                    </div>
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-10 text-white/60 text-lg leading-relaxed font-medium">
                          {item.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
